import { createContext, useContext, useEffect, useState } from "react";
import { Schedule } from "../types";
import { useHomeTeam } from "./useHomeTeam";

type ScheduleContextType = {
  loading: boolean;
  schedule: Schedule;
  error: string | null;
};

const ScheduleContext = createContext<ScheduleContextType>({
  loading: false,
  schedule: null,
  error: null,
});

export function ScheduleProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { code } = useHomeTeam();

  const schedules = {
    CSP: "/schedule/2025-fall-csp.json",
    HNS: "/schedule/2025-fall-hns.json",
    DAD: "/schedule/2025-fall-dad.json",
    DSE: "/schedule/2025-fall-dse.json",
  };

  useEffect(() => {
    const fetchSchedule = async () => {
      setLoading(true);
      setSchedule(null);
      setError(null);

      try {
        const url = schedules[code] || `/schedule/2026-02-15.json`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch schedule`);
        }
        const json = await response.json();
        const scheduleData = json as Schedule;

        const convertedShedule = {
          matches: scheduleData.matches.map((match) => {
            const startDate = new Date(`${match.date}T18:00:00`);
            const endDate = startDate.getTime() + 7 * 60 * 60 * 1000;
            return {
              ...match,
              date: startDate,
              endDate: new Date(endDate),
            };
          }),
        };

        setSchedule(convertedShedule);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [code]);

  return (
    <ScheduleContext.Provider value={{ loading, schedule, error }}>
      {children}
    </ScheduleContext.Provider>
  );
}

export const useSchedule = () => {
  return useContext(ScheduleContext);
};
